import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {
  createCustomSyntax,
  SyntaxConfigurationKey,
  VendorModificationSyntax,
} from 'molbio/monomers/MonomersDAO';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';

function undefIfEmpty(input: string): string | undefined {
  return input.trim().length === 0 ? undefined : input;
}

export default function SyntaxCreationModal(props: {
  onRequestClose: () => void;
  onRequestSubmit: (newSyntax: VendorModificationSyntax) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [isCaseSensitive, setIsCaseSensitive] = useState(true);
  const [usePrefilterTerminals, setUsePrefilterTerminals] = useState(false);
  const [useNullifyPhosphate, setUseNullifyPhosphate] = useState(false);
  const [useSharedDelimiter, setUseSharedDelimiter] = useState(false);
  const [fivePrimeLeftDelimiter, setFivePrimeLeftDelimiter] = useState<string>('');
  const [fivePrimeRightDelimiter, setFivePrimeRightDelimiter] = useState<string>('');
  const [threePrimeLeftDelimiter, setThreePrimeLeftDelimiter] = useState<string>('');
  const [threePrimeRightDelimiter, setThreePrimeRightDelimiter] = useState<string>('');
  const [maxDepth, setMaxDepth] = useState(1);
  const [sharedDelimiter, setSharedDelimiter] = useState<string>('');

  async function submit(): Promise<void> {
    const syntaxData = {
      name,
      isCaseSensitive,
      configuration: {} as VendorModificationSyntax['configuration'],
    };

    if (usePrefilterTerminals) {
      syntaxData.configuration[SyntaxConfigurationKey.PREFILTER_TERMINALS] = {
        five_prime_left_delimiter: undefIfEmpty(fivePrimeLeftDelimiter),
        five_prime_right_delimiter: fivePrimeRightDelimiter,
        three_prime_left_delimiter: threePrimeLeftDelimiter,
        three_prime_right_delimiter: undefIfEmpty(threePrimeRightDelimiter),
        max_depth: maxDepth,
      };
    }
    if (useNullifyPhosphate) {
      syntaxData.configuration[SyntaxConfigurationKey.NULLIFY_ENDING_PHOSPHATE] = {};
    }
    if (useSharedDelimiter) {
      syntaxData.configuration[SyntaxConfigurationKey.USE_SHARED_DELIMITER] = {delimiter: sharedDelimiter};
    }

    const newSyntax = await createCustomSyntax(syntaxData);
    props.onRequestSubmit(newSyntax);
  }

  const canSubmit =
    name.length > 0 &&
    (!usePrefilterTerminals || (fivePrimeRightDelimiter.length > 0 && threePrimeLeftDelimiter.length > 0)) &&
    (!useSharedDelimiter || sharedDelimiter.length === 1);

  return (
    <Modal
      title="Create custom modification notation"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={submit}
      buttons={[
        {
          text: 'Save',
          submit: true,
          disabled: !canSubmit,
        },
      ]}
    >
      <TextField
        className="u-small-spacing-bottom"
        label="Name"
        value={name}
        isRequired={true}
        onChange={(e: InputChangeEvent) => {
          setName(e.target.value);
        }}
      />
      <CheckboxInput
        className="u-small-spacing-bottom"
        id="case-sensitive"
        label="Case sensitive?"
        checked={isCaseSensitive}
        onChange={(e) => {
          setIsCaseSensitive(e.target.checked);
        }}
      />
      <CheckboxInput
        className="u-small-spacing-bottom"
        id="use-shared-delimiter"
        label="Use shared delimiter?"
        checked={useSharedDelimiter}
        onChange={(e) => {
          setUseSharedDelimiter(e.target.checked);
        }}
      />
      {useSharedDelimiter && (
        <>
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            supportingInfo={
              <span>
                A shared delimiter is a single character that separates adjacent tokens (for example,{' '}
                <span className={css(styles.inlinePre)}>ABC,DEF</span> as opposed to{' '}
                <span className={css(styles.inlinePre)}>/ABC//DEF/</span> or{' '}
                <span className={css(styles.inlinePre)}>[ABC][DEF]</span>). It is not necessary to include the
                delimiter when defining individual aliases.
              </span>
            }
          />
          <div style={{width: '25%'}}>
            <TextField
              className="u-small-spacing-bottom u-small-spacing-top u-small-spacing-right"
              label="Delimiter"
              isRequired={true}
              value={sharedDelimiter}
              onChange={(e: InputChangeEvent) => {
                if (e.target.value.length <= 1) {
                  setSharedDelimiter(e.target.value);
                }
              }}
            />
          </div>
        </>
      )}
      <CheckboxInput
        className="u-small-spacing-bottom"
        id="use-prefilter-terminals"
        label="Extract terminals to schema fields?"
        checked={usePrefilterTerminals}
        onChange={(e) => {
          setUsePrefilterTerminals(e.target.checked);
        }}
      />
      {usePrefilterTerminals && (
        <>
          <div className="u-flex-display">
            <TextField
              className="u-small-spacing-bottom u-small-spacing-right"
              label="5' left delimiter"
              value={fivePrimeLeftDelimiter}
              onChange={(e: InputChangeEvent) => {
                setFivePrimeLeftDelimiter(e.target.value);
              }}
            />
            <TextField
              className="u-small-spacing-bottom u-small-spacing-right"
              label="5' right delimiter"
              value={fivePrimeRightDelimiter}
              isRequired={true}
              onChange={(e: InputChangeEvent) => {
                setFivePrimeRightDelimiter(e.target.value);
              }}
            />
            <TextField
              className="u-small-spacing-bottom u-small-spacing-right"
              label="3' left delimiter"
              value={threePrimeLeftDelimiter}
              isRequired={true}
              onChange={(e: InputChangeEvent) => {
                setThreePrimeLeftDelimiter(e.target.value);
              }}
            />
            <TextField
              className="u-small-spacing-bottom"
              label="3' right delimiter"
              value={threePrimeRightDelimiter}
              onChange={(e: InputChangeEvent) => {
                setThreePrimeRightDelimiter(e.target.value);
              }}
            />
          </div>
          <div className="u-spacing-bottom u-spacing-left">
            <CheckboxInput
              id="allow-multiple-terminals"
              label="Allow up to 2 terminals at each end?"
              checked={maxDepth > 1}
              onChange={(e) => {
                setMaxDepth(Number(e.target.checked) + 1);
              }}
            />
          </div>
        </>
      )}
      <CheckboxInput
        id="use-nullify-phosphate"
        label="Always set last phosphate to None?"
        checked={useNullifyPhosphate}
        onChange={(e) => {
          setUseNullifyPhosphate(e.target.checked);
        }}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  inlinePre: {
    fontFamily: 'monospace',
    backgroundColor: 'white',
  },
});
